import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import shp from "shpjs";
import * as tj from "@mapbox/togeojson";
import csv from "csv2geojson";
import JSZip from "jszip";
import { v4 as uuid } from "uuid";
import { GeoService, NormalizationResult } from "../internal-services/geo.service";
import { ViewerConstants } from "../../../shared/model/viewer-constants";

@Injectable({
  providedIn: "root",
})
export class FileReaderService {
  static getFileExtension(fileName): string {
    const extension = fileName.split(".").pop();
    return extension.replace(".", "").toLowerCase();
  }

  /**
   * Prepare input by normalizing and enriching the result.
   * Always returns a composed object:
   *  - data: FeatureCollection in Mercator with metadata (or null if none valid)
   *  - status: "ok" | "warning" | "error"
   *  - errors: array of { feature, message }
   */
  prepareGeoJson(geojson: any, fileName: string): NormalizationResult {
    try {
      const normalization = GeoService.normalizeFeatures(geojson);
      if (!normalization.data || normalization.status === "error") {
        return normalization;
      }
      let featureCollection = GeoService.toMercator(normalization.data, false);
      featureCollection.features.forEach((feature) => {
        // 'geometry' is a RESERVED OL Feature property: OL's readFeatures runs
        // setProperties(features) AFTER setGeometry, so a stray (backend-sent) null or
        // duplicated geometry inside properties CLOBBERS the parsed geometry with null and
        // LayerFactory then crashes cloning it. Strip the key unconditionally when present.
        if (feature?.properties && "geometry" in feature.properties) {
          delete feature.properties.geometry;
        }
        (feature as any).id = uuid();
      });
      const bbox = GeoService.featureCollectionBbox(featureCollection);
      // Merge app-generated metadata into any existing top-level properties from the source file
      const originalProperties = (featureCollection as any).properties ?? {};
      (featureCollection as any).properties = {
        ...originalProperties,
        epsg: ViewerConstants.PROJECTIONS.WGS84,
        id: uuid(),
        name: fileName,
        bbox: bbox,
        fileType: FileReaderService.getFileExtension(fileName),
      };
      // Setup overlay properties for CSV/ZIP only
      const ext = FileReaderService.getFileExtension(fileName);
      if (featureCollection.features.length && ["csv", "zip"].includes(ext)) {
        const propertyKeys = Object.keys(featureCollection.features[0].properties || {});
        (featureCollection as any).properties.customOverlayProperties = {
          iconSrc: "assets/images/csv_pin_btn.svg",
          layerProperties: propertyKeys,
          primaryField: propertyKeys[0],
          secondaryField: propertyKeys[1],
        };
      }
      normalization.data = featureCollection;
      return normalization;
    } catch (err: any) {
      const msg = err?.message || "Unknown error while preparing GeoJSON";
      console.warn("Failed to prepare GeoJSON:", msg);
      return {
        data: null,
        status: "error",
        errors: [{ feature: null, message: msg }],
      };
    }
  }

  readFile(file): Observable<NormalizationResult> {
    const fileSize = file.size / 1024 / 1024;
    if (!file || fileSize > ViewerConstants.MAX_FILE_SIZE_UPLOAD) {
      return throwError(
        `Oops! Your file size is too large. Make sure it’s less than ${ViewerConstants.MAX_FILE_SIZE_UPLOAD} MB.`,
      );
    }
    const extension = FileReaderService.getFileExtension(file.name);
    switch (extension) {
      case "zip": {
        return this.readShapeFile(file);
      }
      case "kml": {
        return this.readKMLFile(file);
      }
      case "kmz": {
        return this.readKMZFile(file);
      }
      case "csv": {
        return this.readCSVFile(file);
      }
      case "wkt": {
        return this.readWKTFile(file);
      }
      case "geojson": {
        return this.readGeoJSONFile(file);
      }
      default: {
        return throwError("Oops! Your file extension is unsupported");
      }
    }
  }

  readShapeFile(file): Observable<NormalizationResult> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      let buffered = null;
      reader.addEventListener("load", (event) => {
        buffered = event.target;
        shp(buffered.result)
          .then((geojson) => {
            try {
              subscriber.next(this.prepareGeoJson(geojson, file.name));
            } catch (e) {
              subscriber.error(e.message);
            }
          })
          .catch((e) => {
            subscriber.error("Can't read this file, is this a zip file ?");
          });
      });
      reader.readAsArrayBuffer(file);
    });
  }

  readCSVFile(file): Observable<NormalizationResult> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      let buffered = null;
      reader.addEventListener("load", (event) => {
        buffered = event.target;
        csv.csv2geojson(buffered.result, { delimiter: "auto" }, (err, geojson) => {
          try {
            if (err) {
              throw new Error(err);
            }
            subscriber.next(this.prepareGeoJson(this.enrichFeaturesFromCentroid(geojson), file.name));
            subscriber.complete();
          } catch (e) {
            subscriber.error("Oops! Our system couldn’t read your file. Please download the pre-formatted template.");
          }
        });
      });
      reader.readAsText(file);
    });
  }

  /**
   * If csv2geojson could not detect geometry columns, try to build Point geometry
   * from a "centroid" property formatted as "lon,lat" (e.g. exported by this app).
   */
  private enrichFeaturesFromCentroid(geojson: any): any {
    if (!geojson?.features?.length) return geojson;

    const needsEnrichment = geojson.features.some((f) => !f.geometry && f.properties?.centroid);
    if (!needsEnrichment) return geojson;

    return {
      ...geojson,
      features: geojson.features
        .map((feature) => {
          if (feature.geometry) return feature;
          const raw = feature.properties?.centroid?.toString() ?? "";
          const parts = raw.split(",").map(Number);
          if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
            return { ...feature, geometry: { type: "Point", coordinates: [parts[0], parts[1]] } };
          }
          return feature;
        })
        .filter((f) => f.geometry),
    };
  }

  readKMLFile(file): Observable<NormalizationResult> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      let buffered = null;
      reader.addEventListener("load", (event) => {
        buffered = event.target;
        const kmlFileDom = new DOMParser().parseFromString(buffered.result, "text/xml");
        try {
          subscriber.next(this.prepareGeoJson(tj.kml(kmlFileDom), file.name));
          subscriber.complete();
        } catch (e) {
          subscriber.error(e.message);
        }
      });
      reader.readAsText(file);
    });
  }

  readKMZFile(file): Observable<NormalizationResult> {
    const getDom = (xml) => new DOMParser().parseFromString(xml, "text/html");
    const getExtension = (fileName) => fileName.split(".").pop();

    const getKmlDom = (kmzFile) => {
      const zip = new JSZip();
      return zip.loadAsync(kmzFile).then((z) => {
        let kmlDom = null;
        z.forEach((relPath, f) => {
          if (getExtension(relPath) === "kml" && kmlDom === null) {
            kmlDom = f.async("string").then(getDom);
          }
        });
        return kmlDom || Promise.reject("No kml file found");
      });
    };
    return new Observable((subscriber) => {
      getKmlDom(file).then((kmlDom) => {
        const geoJsonObject = tj.kml(kmlDom);
        subscriber.next(this.prepareGeoJson(geoJsonObject, file.name));
        subscriber.complete();
        subscriber.error((e) => e.message);
      });
    });
  }

  readGeoJSONFile(file): Observable<NormalizationResult> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        const content = event.target?.result?.toString() ?? "";
        try {
          subscriber.next(this.prepareGeoJson(JSON.parse(content), file.name));
          subscriber.complete();
        } catch (e) {
          subscriber.error(e.message);
        }
      });
      reader.readAsText(file);
    });
  }

  readWKTFile(file): Observable<NormalizationResult> {
    return new Observable((subscriber) => {
      const reader = new FileReader();
      reader.addEventListener("load", (event) => {
        const content = event.target?.result?.toString() ?? "";
        try {
          const featureCollection = GeoService.wktToFeatureCollection(content, false);
          subscriber.next(this.prepareGeoJson(featureCollection, file.name));
          subscriber.complete();
        } catch (e) {
          subscriber.error(e.message);
        }
      });
      reader.readAsText(file);
    });
  }
}
